import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../context/UserContext";
import { authApi, profilesApi } from "../../api";
import { isBlockedError, getBlockedErrorMessage } from "../../utils/moderationErrors";
import HrnbForm from "../controls/HrnbForm";
import HrnbFormControl from "../controls/HrnbFormControl";
import HrnbButton from "../controls/HrnbButton";
import HrnbRadioButton from "../controls/HrnbRadioButton";
import HrnbDatePicker from "../controls/HrnbDatePicker";
import { RegisterIcon } from "../../icons";

export default function HrnbRegisterForm({ onClose, ...props }) {
    const [username, setUsername] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [birthDate, setBirthDate] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [gender, setGender] = useState("");
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const {setCurrentUser} = useContext(UserContext);
    const navigate = useNavigate();

    const validateEmail = (value) => {
        const regex = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;
        return regex.test(value);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setErrors({});
        let formIsValid = true;
        const newErrors = {};

        if (!username || username.length < 3) {
            newErrors.username = "Имя пользователя должно быть не менее 3 символов";
            formIsValid = false;
        }

        if (!validateEmail(email)) {
            newErrors.email = "Некорректный адрес электронной почты";
            formIsValid = false;
        }

        if (
            !password ||
            password.length < 8 ||
            !/[A-Z]/.test(password) ||
            !/\d/.test(password)
        ) {
            newErrors.password = "Пароль: минимум 8 символов, цифра и заглавная буква";
            formIsValid = false;
        }

        if (password !== confirmPassword) {
            newErrors.confirmPassword = "Пароли не совпадают";
            formIsValid = false;
        }

        if (!gender) {
            newErrors.gender = "Выберите пол";
            formIsValid = false;
        }

        if (birthDate) {
            const selected = new Date(birthDate);
            const now = new Date();
            if (selected >= now) {
                newErrors.birthDate = "Дата рождения должна быть в прошлом";
                formIsValid = false;
            }
        }

        setErrors(newErrors);

        if (!formIsValid) {
            return;
        }

        setLoading(true);
        try {
            const payload = {
                username,
                email,
                password,
                gender: gender === "male" ? "male" : "female",
            };
            if (birthDate) payload.birthDate = birthDate;
            if (phoneNumber) payload.phoneNumber = phoneNumber;

            const data = await authApi.register(payload);

            if (data?.accessToken) {
                try {
                    const profile = await profilesApi.me();
                    if (setCurrentUser) setCurrentUser(profile);
                } catch (err) {
                }
                setSuccessMessage("Вы успешно зарегистрировались! Выполняем вход и перенаправляем...");
                navigate("/cabinet");
                if (onClose) onClose();
            } else {
                setSuccessMessage("Вы успешно зарегистрировались! Подтвердите аккаунт по ссылке в письме.");
            }
        } catch (err) {
            let msg;
            if (isBlockedError(err)) {
                msg = getBlockedErrorMessage(err);
            } else {
                msg =
                    err?.response?.data?.message ||
                    err?.response?.data ||
                    err?.message ||
                    "Ошибка регистрации";
            }
            setErrors((prev) => ({...prev, submit: msg}));
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <HrnbForm title="Регистрация" onSubmit={handleSubmit}>
                <HrnbFormControl
                    label="Имя пользователя"
                    value={username}
                    error={errors.username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Ваше имя пользователя"
                    autoFocus
                />
                <HrnbFormControl
                    label="Электронная почта"
                    type="text"
                    value={email}
                    error={errors.email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="example@mail.ru"
                    style={{ marginTop: 12 }}
                />
                <HrnbFormControl
                    label="Пароль"
                    as="password"
                    value={password}
                    error={errors.password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Минимум 8 символов"
                    minLength={8}
                    style={{ marginTop: 12 }}
                />
                <HrnbFormControl
                    label="Подтверждение пароля"
                    as="password"
                    value={confirmPassword}
                    error={errors.confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Повторите пароль"
                    minLength={8}
                    style={{ marginTop: 12 }}
                />
                <HrnbDatePicker
                    label="Дата рождения"
                    value={birthDate}
                    onChange={setBirthDate}
                    maxDate={new Date().toISOString().slice(0, 10)}
                    error={errors.birthDate}
                    style={{ marginTop: 12 }}
                    placeholder="Выберите дату рождения"
                />
                <HrnbFormControl
                    label="Номер телефона"
                    type="text"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="+79001234567"
                    style={{ marginTop: 12 }}
                />
                <div style={{ marginTop: 12, display: "flex", gap: 12, flexWrap: "wrap" }}>
                    <HrnbRadioButton
                        label="Мужской"
                        name="gender"
                        value="male"
                        checked={gender === "male"}
                        onChange={() => setGender("male")}
                    />
                    <HrnbRadioButton
                        label="Женский"
                        name="gender"
                        value="female"
                        checked={gender === "female"}
                        onChange={() => setGender("female")}
                    />
                </div>
                {errors.submit && <div style={{ color: "red", marginTop: 12 }}>{errors.submit}</div>}
                <HrnbButton
                    type="submit"
                    style={{ width: "100%", marginTop: 20 }}
                    disabled={loading}
                    icon={<RegisterIcon />}
                >
                    {loading ? "Регистрация..." : "Зарегистрироваться"}
                </HrnbButton>
            </HrnbForm>
            {successMessage && <p style={{ color: "green", marginTop: 16 }}>{successMessage}</p>}
        </>
    );


}